import { z } from 'zod';
/**
 * Risk register, incidents, corrective actions, vendor/subprocessor register,
 * data-processing inventory and DPIA workflows. All synthetic content; the
 * inventory mirrors docs/security/DATA_CLASSIFICATION.md as data, so the
 * privacy posture is queryable, not just documented.
 */
export const RISK_LIKELIHOODS = ['rare', 'possible', 'likely'];
export const RISK_IMPACTS = ['minor', 'moderate', 'severe'];
export const RISK_STATUSES = ['open', 'mitigating', 'accepted', 'closed'];
export const RiskLikelihoodSchema = z.enum(RISK_LIKELIHOODS);
export const RiskImpactSchema = z.enum(RISK_IMPACTS);
export const RiskStatusSchema = z.enum(RISK_STATUSES);
/** likelihood × impact → 1..9; bands: 1-2 low, 3-4 medium, 6-9 high. */
export function riskRating(likelihood, impact) {
    return (RISK_LIKELIHOODS.indexOf(likelihood) + 1) * (RISK_IMPACTS.indexOf(impact) + 1);
}
export const RiskSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    description: z.string(),
    likelihood: RiskLikelihoodSchema,
    impact: RiskImpactSchema,
    rating: z.number().int().min(1).max(9),
    status: RiskStatusSchema,
    ownerDisplayName: z.string().nullable(),
    acceptanceReason: z.string().nullable(),
    reviewDueAt: z.iso.datetime().nullable(),
    version: z.number().int(),
    updatedAt: z.iso.datetime(),
});
export const RiskInputSchema = z
    .object({
    title: z.string().min(5).max(200),
    description: z.string().min(10).max(2000),
    likelihood: RiskLikelihoodSchema,
    impact: RiskImpactSchema,
    reviewDueAt: z.iso.datetime().optional(),
})
    .strict();
export const INCIDENT_STATUSES = ['open', 'contained', 'resolved', 'closed'];
export const IncidentStatusSchema = z.enum(INCIDENT_STATUSES);
export const INCIDENT_TRANSITIONS = {
    open: ['contained'],
    contained: ['resolved'],
    resolved: ['closed'],
    closed: [],
};
export const IncidentSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    summary: z.string(),
    severity: z.enum(['low', 'medium', 'high']),
    status: IncidentStatusSchema,
    riskId: z.uuid().nullable(),
    openedAt: z.iso.datetime(),
    closedAt: z.iso.datetime().nullable(),
    version: z.number().int(),
});
export const CorrectiveActionSchema = z.object({
    id: z.uuid(),
    incidentId: z.uuid().nullable(),
    riskId: z.uuid().nullable(),
    title: z.string(),
    status: z.enum(['todo', 'in_progress', 'done']),
    dueAt: z.iso.datetime().nullable(),
    ownerDisplayName: z.string().nullable(),
    version: z.number().int(),
});
export const VendorSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    purpose: z.string(),
    isSubprocessor: z.boolean(),
    dataCategories: z.array(z.string()),
    residency: z.string(),
    dpaSigned: z.boolean(),
    dpaReference: z.string().nullable(),
    reviewDueAt: z.iso.datetime().nullable(),
    version: z.number().int(),
});
export const ProcessingActivitySchema = z.object({
    id: z.uuid(),
    activity: z.string(),
    dataCategories: z.array(z.string()),
    classification: z.enum(['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED']),
    legalBasis: z.string(),
    retention: z.string(),
    systemOfRecord: z.string(),
});
export const DPIA_STATUSES = ['draft', 'in_review', 'approved', 'rejected'];
export const DpiaStatusSchema = z.enum(DPIA_STATUSES);
export const DpiaSchema = z.object({
    id: z.uuid(),
    title: z.string(),
    scope: z.string(),
    status: DpiaStatusSchema,
    assessment: z.string().nullable(),
    decidedByDisplayName: z.string().nullable(),
    decidedAt: z.iso.datetime().nullable(),
    version: z.number().int(),
});
//# sourceMappingURL=risk.js.map